import {PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, useState} from 'react';

const POSITION_STORAGE_KEY = 'askAiFloatingButtonPosition';
const VIEWPORT_MARGIN = 8;
const DRAG_THRESHOLD_PX = 4;

export interface FloatingPosition {
  right: number;
  bottom: number;
}

interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  origin: FloatingPosition;
  moved: boolean;
}

interface ViewportSize {
  width: number;
  height: number;
}

function readStoredPosition(): FloatingPosition | null {
  try {
    const stored = localStorage.getItem(POSITION_STORAGE_KEY);
    if (!stored) {
      return null;
    }
    const parsed = JSON.parse(stored) as Partial<FloatingPosition>;
    if (typeof parsed.right !== 'number' || typeof parsed.bottom !== 'number') {
      return null;
    }
    return {right: parsed.right, bottom: parsed.bottom};
  } catch {
    return null;
  }
}

function storePosition(position: FloatingPosition): void {
  try {
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
  } catch {
    return;
  }
}

function readViewportSize(): ViewportSize {
  return {width: window.innerWidth, height: window.innerHeight};
}

function clampPosition(
  position: FloatingPosition,
  element: HTMLElement | null,
  viewport: ViewportSize,
  minimumBottom: number,
): FloatingPosition {
  const width = element?.offsetWidth ?? 0;
  const height = element?.offsetHeight ?? 0;
  const maxRight = Math.max(VIEWPORT_MARGIN, viewport.width - width - VIEWPORT_MARGIN);
  const maxBottom = Math.max(minimumBottom, viewport.height - height - VIEWPORT_MARGIN);

  return {
    right: Math.round(Math.min(Math.max(position.right, VIEWPORT_MARGIN), maxRight)),
    bottom: Math.round(Math.min(Math.max(position.bottom, minimumBottom), maxBottom)),
  };
}

export function useDraggableFloatingButton(defaultPosition: FloatingPosition) {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [customPosition, setCustomPosition] = useState<FloatingPosition | null>(readStoredPosition);
  const [viewport, setViewport] = useState<ViewportSize>(readViewportSize);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<DragState | null>(null);
  const clickSuppressedRef = useRef(false);

  const position = customPosition
    ? clampPosition(customPosition, element, viewport, defaultPosition.bottom)
    : defaultPosition;
  const positionRef = useRef(position);
  positionRef.current = position;

  useEffect(() => {
    const onResize = () => setViewport(readViewportSize());
    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, []);

  const elementRef = useCallback((node: HTMLDivElement | null) => {
    setElement(node);
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0 || !element) {
        return;
      }
      const rect = element.getBoundingClientRect();
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        origin: {right: window.innerWidth - rect.right, bottom: window.innerHeight - rect.bottom},
        moved: false,
      };
    },
    [element],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }
      const deltaX = event.clientX - drag.startX;
      const deltaY = event.clientY - drag.startY;
      if (!drag.moved) {
        if (Math.abs(deltaX) + Math.abs(deltaY) < DRAG_THRESHOLD_PX) {
          return;
        }
        drag.moved = true;
        setIsDragging(true);
        element?.setPointerCapture(event.pointerId);
      }
      setCustomPosition({right: drag.origin.right - deltaX, bottom: drag.origin.bottom - deltaY});
    },
    [element],
  );

  const finishDrag = useCallback(
    (event: ReactPointerEvent<HTMLElement>, suppressClick: boolean) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }
      dragRef.current = null;
      if (element?.hasPointerCapture(event.pointerId)) {
        element.releasePointerCapture(event.pointerId);
      }
      if (!drag.moved) {
        return;
      }
      clickSuppressedRef.current = suppressClick;
      setIsDragging(false);
      setCustomPosition(positionRef.current);
      storePosition(positionRef.current);
    },
    [element],
  );

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>) => finishDrag(event, true), [finishDrag]);

  const onPointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => finishDrag(event, false),
    [finishDrag],
  );

  const shouldIgnoreClick = useCallback(() => {
    if (!clickSuppressedRef.current) {
      return false;
    }
    clickSuppressedRef.current = false;
    return true;
  }, []);

  return {
    elementRef,
    position,
    isDragging,
    shouldIgnoreClick,
    dragHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    },
  };
}
